// web/src/registry/features/_part-6.ts —— FEATURES 的第 6/6 段（17 个元素：telephony / workflow / data-collection / identity / integration / compliance / audit / diagnostics）
// 拆自 web/src/registry/features.ts（元素原样搬运、顺序不变）；由入口按顺序展开组合，故本片是**内部片**，不对外导出。
import type { Feature } from '../features';

export const FEATURES_PART_6: Feature[] = [

  {
    id: 'telephony.whitelist',
    title: '电话白名单',
    domain: 'telephony',
    scope: 'platform',
    type: 'config',
    route: '/platform/telephony/whitelist',
    permission: 'telephony.settings',
    priority: 'P0',
    issue: '试运行期限制可拨/可接号码的安全控制：zitch 执行层双向拦截（呼入主叫/被叫 + 呼出被叫），故归通信能力而非外呼业务域',
  },

  // 流程中心
  {
    id: 'workflow.flows',
    title: '业务流程',
    domain: 'workflow',
    scope: 'platform',
    type: 'platform-capability',
    route: '/platform/workflow/flows',
    legacyLocations: ['/system/flows'],
    permission: 'workflow.flows',
    nav: { section: 'platform', group: 'workflow', order: 1 },
    priority: 'P0',
    issue: '对话流程引擎原埋在 /system，且与审核流程命名冲突',
  },
  {
    id: 'workflow.approvals',
    title: '审批流程',
    domain: 'workflow',
    scope: 'platform',
    type: 'platform-capability',
    route: '/platform/workflow/approvals',
    legacyLocations: ['/system/workflows'],
    permission: 'workflow.approvals',
    nav: { section: 'platform', group: 'workflow', order: 2 },
    priority: 'P0',
    issue: '审批流引擎原埋在 /system；业务侧只保留绑定（如 ticketing.settings.approval）',
  },

  // 数据采集
  {
    id: 'data-collection.fields',
    title: '字段定义',
    domain: 'data-collection',
    scope: 'platform',
    type: 'config',
    route: '/platform/data-collection/fields',
    legacyLocations: ['/system/collect-fields'],
    permission: 'data-collection.fields',
    nav: { section: 'platform', group: 'data-collection', order: 1 },
    priority: 'P0',
    issue: '共享字段字典原埋在工单配置；被流程/采集/工单三方复用',
  },
  {
    id: 'data-collection.rules',
    title: '采集规则',
    domain: 'data-collection',
    scope: 'platform',
    type: 'platform-capability',
    route: '/platform/data-collection/rules',
    legacyLocations: ['/system/collection'],
    permission: 'data-collection.rules',
    nav: { section: 'platform', group: 'data-collection', order: 2 },
    priority: 'P0',
    issue: '采集 Schema 原埋在工单配置；运行在坐席辅助/媒体管线，被多域消费',
  },

  // ───────────────────────────── 系统治理 ─────────────────────────────

  // 用户与组织
  {
    id: 'identity.users',
    title: '用户',
    domain: 'identity',
    scope: 'system',
    type: 'resource',
    route: '/system/identity/users',
    legacyLocations: ['/account?tab=users'],
    permission: 'identity.users',
    nav: { section: 'system', group: 'identity', order: 1 },
    priority: 'P1',
    issue: '原为 /account 页内 Tab，是独立 Resource',
  },
  {
    id: 'identity.agents',
    title: '坐席',
    domain: 'identity',
    scope: 'system',
    type: 'resource',
    route: '/system/identity/agents',
    legacyLocations: ['/account?tab=agents'],
    permission: 'identity.agents',
    nav: { section: 'system', group: 'identity', order: 2 },
    priority: 'P1',
    issue: '原为 /account 页内 Tab',
  },
  {
    id: 'identity.skill-groups',
    title: '技能组',
    domain: 'identity',
    scope: 'platform',
    type: 'resource',
    route: '/system/identity/agents/skill-groups',
    legacyLocations: ['/system/skill-groups', '/inbound/skill-groups', '/inbound/settings?tab=skill-groups'],
    permission: 'inbound.skill-groups',
    priority: 'P1',
    issue: '坐席的业务分组 + 排队队列本身（坐席分组/排班/转人工/外呼分配/工单派单/话术可见性都在消费它，按 §2 是 PLATFORM），按「坐席菜单里的资源」落为「坐席」页内 Tab；升级为平台域待定。权限码仍为 inbound.skill-groups（权限码整体重构时一并改名）',
  },
  {
    id: 'identity.orgs',
    title: '组织',
    domain: 'identity',
    scope: 'system',
    type: 'resource',
    route: '/system/identity/orgs',
    legacyLocations: ['/account?tab=orgs'],
    permission: 'identity.orgs',
    nav: { section: 'system', group: 'identity', order: 3 },
    priority: 'P1',
    issue: '原为 /account 页内 Tab',
  },
  {
    id: 'identity.roles',
    title: '角色',
    domain: 'identity',
    scope: 'system',
    type: 'resource',
    route: '/system/identity/roles',
    legacyLocations: ['/account?tab=roles'],
    permission: 'identity.roles',
    nav: { section: 'system', group: 'identity', order: 4 },
    priority: 'P1',
    issue: '原为 /account 页内 Tab，且长期无独立入口',
  },
  // 集成
  // 「开放平台」（原 `/system/integration/open-platform`）已整体下线：接口（`/api/open/v1/*`）、
  // 应用与密钥管理页、菜单码与内置角色授权一并摘除，故本段不再登记该 Feature。
  // 外部数据源仍在用（配置面 + 执行面各自走敏感能力）。
  {
    id: 'integration.data-sources',
    title: '外部数据源',
    domain: 'integration',
    scope: 'system',
    type: 'resource',
    route: '/system/integration/data-sources',
    legacyLocations: ['/system/data-sources'],
    permission: 'integration.data-sources',
    nav: { section: 'system', group: 'integration', order: 2 },
    priority: 'P2',
  },

  // 安全与合规
  {
    id: 'compliance.settings',
    title: '数据合规',
    domain: 'compliance',
    scope: 'system',
    type: 'config',
    route: '/system/compliance/settings',
    legacyLocations: ['/system/sys-settings?tab=privacy'],
    permission: 'compliance.settings',
    nav: { section: 'system', group: 'compliance', order: 1 },
    priority: 'P0',
    issue: '合规配置原藏在 /system/sys-settings 页签',
  },
  {
    id: 'compliance.security',
    title: '安全认证',
    domain: 'compliance',
    scope: 'system',
    type: 'config',
    route: '/system/compliance/security',
    legacyLocations: ['/system/identity/security', '/system/sys-settings?tab=security'],
    permission: 'compliance.security',
    nav: { section: 'system', group: 'compliance', order: 2 },
    priority: 'P1',
    issue: '2FA/演示上传口令等安全配置，从系统设置页签独立为安全与合规域页面',
  },
  {
    id: 'compliance.egress',
    title: '数据出域',
    domain: 'compliance',
    scope: 'system',
    type: 'governance',
    route: '/system/compliance/egress',
    legacyLocations: ['/system/egress'],
    permission: 'compliance.egress',
    nav: { section: 'system', group: 'compliance', order: 3 },
    priority: 'P2',
  },

  // 审计
  {
    id: 'audit.logs',
    title: '操作审计',
    domain: 'audit',
    scope: 'system',
    type: 'governance',
    route: '/system/audit/logs',
    legacyLocations: ['/system/audit-logs'],
    permission: 'audit.logs',
    nav: { section: 'system', group: 'audit', order: 1 },
    priority: 'P2',
  },
  {
    id: 'audit.ai',
    title: 'AI 调用审计',
    domain: 'audit',
    scope: 'system',
    type: 'governance',
    route: '/system/audit/ai',
    legacyLocations: ['/aicenter/llm-history'],
    permission: 'audit.ai',
    nav: { section: 'system', group: 'audit', order: 2 },
    priority: 'P1',
    issue: '与 ai.usage 同源不同义：审计回答“谁调用/是否留痕/敏感数据”，用量回答“调用量/成本”',
  },

  // 故障诊断
  {
    id: 'diagnostics.diag',
    title: '故障诊断',
    domain: 'diagnostics',
    scope: 'system',
    type: 'analytics',
    route: '/system/diagnostics',
    legacyLocations: ['/system/diag'],
    permission: 'diagnostics.diag',
    nav: { section: 'system', group: 'diagnostics', order: 99, flat: true },
    priority: 'P1',
    issue: '运维诊断工具原挂在系统管理，语义上非严格治理，但单功能不单开一级区',
  },

  // 公告中心
  {
    id: 'notice.manage',
    title: '公告管理',
    domain: 'notice',
    scope: 'system',
    type: 'resource',
    route: '/system/notices',
    permission: 'notice.manage',
    nav: { section: 'system', group: 'notice', order: 1, flat: true },
    priority: 'P2',
  },];
